/** XeAgents.ai - Elite Vending Machine Support Dashboard **/
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  MapPin,
  Wrench,
  Search,
  RefreshCw,
  TrendingUp,
  Users,
  Bot,
  Mic,
  Activity,
  Plus,
  Settings,
  Play,
  Pause,
  BarChart3
} from "lucide-react";
import { useTenant } from "@/components/TenantProvider";

// Types
interface Agent {
  id: string;
  name: string;
  description: string;
  voiceId: string;
  isActive: boolean;
  createdAt: string;
}

interface Call {
  id: string;
  agentId: string;
  status: string;
  duration: number;
  direction: string;
  createdAt: string;
}

// Mock data for machines - would come from Elite's inventory system
const mockMachines = [
  { id: "EVM-001", location: "Main Street Mall", status: "operational", lastService: "2025-05-01", stockLevel: 85 },
  { id: "EVM-002", location: "Central Station", status: "needs_service", lastService: "2025-04-15", stockLevel: 42 },
  { id: "EVM-003", location: "Office Park A", status: "operational", lastService: "2025-05-05", stockLevel: 92 },
  { id: "EVM-004", location: "University Campus", status: "offline", lastService: "2025-04-28", stockLevel: 15 },
  { id: "EVM-005", location: "Hospital Lobby", status: "operational", lastService: "2025-05-03", stockLevel: 78 },
];

// Mock support calls data
const mockSupportCalls = [
  { id: 1, machineId: "EVM-002", issue: "Product stuck", status: "resolved", time: "10 mins ago", handledBy: "Customer Support Agent" },
  { id: 2, machineId: "EVM-004", issue: "Not accepting cards", status: "pending", time: "25 mins ago", handledBy: "AI Agent" },
  { id: 3, machineId: "EVM-001", issue: "Refund request", status: "resolved", time: "1 hour ago", handledBy: "Refund Handler Agent" },
  { id: 4, machineId: "EVM-003", issue: "Display issue", status: "in_progress", time: "2 hours ago", handledBy: "Technical Support Agent" },
];

function getStatusBadge(status: string) {
  switch (status) {
    case "operational":
      return <Badge className="bg-green-500">Operational</Badge>;
    case "needs_service":
      return <Badge className="bg-yellow-500">Needs Service</Badge>;
    case "offline":
      return <Badge className="bg-red-500">Offline</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

function getCallStatusBadge(status: string) {
  switch (status) {
    case "resolved":
      return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Resolved</Badge>;
    case "pending":
      return <Badge className="bg-yellow-500"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    case "in_progress":
      return <Badge className="bg-blue-500"><RefreshCw className="h-3 w-3 mr-1" />In Progress</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

function getAgentStatusBadge(isActive: boolean) {
  return isActive ? (
    <Badge className="bg-green-500"><Activity className="h-3 w-3 mr-1" />Active</Badge>
  ) : (
    <Badge variant="secondary"><Pause className="h-3 w-3 mr-1" />Inactive</Badge>
  );
}

export default function VendingSupport() {
  const { config } = useTenant();
  const [searchQuery, setSearchQuery] = useState("");
  const [agentSearchQuery, setAgentSearchQuery] = useState("");

  // Fetch real agents from API
  const { data: agents = [], isLoading: agentsLoading } = useQuery<Agent[]>({
    queryKey: ["/api/agents"],
  });

  // Fetch real calls from API
  const { data: calls = [], isLoading: callsLoading } = useQuery<Call[]>({
    queryKey: ["/api/calls"],
  });

  const filteredMachines = mockMachines.filter(
    machine =>
      machine.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      machine.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAgents = agents.filter(
    agent =>
      agent.name.toLowerCase().includes(agentSearchQuery.toLowerCase()) ||
      agent.description?.toLowerCase().includes(agentSearchQuery.toLowerCase())
  );

  // Calculate agent stats
  const activeAgents = agents.filter(a => a.isActive).length;
  const totalCalls = calls.length;
  const todayCalls = calls.filter(c => {
    const callDate = new Date(c.createdAt);
    const today = new Date();
    return callDate.toDateString() === today.toDateString();
  }).length;

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {config?.branding?.companyName || "Elite"} Support Dashboard
          </h1>
          <p className="text-muted-foreground">
            Monitor machines, manage AI voice agents, and track customer support
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link href="/app/agents">
              <Settings className="h-4 w-4 mr-2" />
              Manage Agents
            </Link>
          </Button>
          <Button asChild>
            <Link href="/app/calls">
              <Phone className="h-4 w-4 mr-2" />
              View All Calls
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Machines</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockMachines.length}</div>
            <p className="text-xs text-muted-foreground">
              {mockMachines.filter(m => m.status === "operational").length} operational
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Voice Agents</CardTitle>
            <Bot className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{agents.length}</div>
            <p className="text-xs text-muted-foreground">
              {activeAgents} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today's Calls</CardTitle>
            <PhoneCall className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{todayCalls}</div>
            <p className="text-xs text-muted-foreground">
              {totalCalls} total calls
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Tickets</CardTitle>
            <Wrench className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mockSupportCalls.filter(c => c.status !== "resolved").length}
            </div>
            <p className="text-xs text-muted-foreground">
              Pending resolution
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Stock Level</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(mockMachines.reduce((acc, m) => acc + m.stockLevel, 0) / mockMachines.length)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Across all machines
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="agents" className="space-y-4">
        <TabsList>
          <TabsTrigger value="agents">
            <Bot className="h-4 w-4 mr-2" />
            Voice Agents
          </TabsTrigger>
          <TabsTrigger value="machines">
            <Package className="h-4 w-4 mr-2" />
            Machine Status
          </TabsTrigger>
          <TabsTrigger value="calls">
            <Phone className="h-4 w-4 mr-2" />
            Support Calls
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Voice Agents Tab */}
        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>AI Voice Agents</CardTitle>
                  <CardDescription>
                    Voice agents handling customer support calls for vending machines
                  </CardDescription>
                </div>
                <Button asChild>
                  <Link href="/app/agents">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Agent
                  </Link>
                </Button>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search agents..."
                  value={agentSearchQuery}
                  onChange={(e) => setAgentSearchQuery(e.target.value)}
                  className="max-w-sm"
                />
              </div>
            </CardHeader>
            <CardContent>
              {agentsLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading agents...</div>
              ) : filteredAgents.length === 0 ? (
                <div className="text-center py-8">
                  <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Voice Agents Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create AI voice agents to handle customer support calls automatically
                  </p>
                  <Button asChild>
                    <Link href="/app/agents">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Agent
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredAgents.map((agent) => (
                    <Card key={agent.id} className="relative">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-primary/10">
                                <Bot className="h-5 w-5" />
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <CardTitle className="text-base">{agent.name}</CardTitle>
                              <CardDescription className="text-xs line-clamp-1">
                                {agent.description || "No description"}
                              </CardDescription>
                            </div>
                          </div>
                          {getAgentStatusBadge(agent.isActive)}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mic className="h-4 w-4" />
                          <span>Voice configured</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="h-4 w-4" />
                          <span>
                            {calls.filter(c => c.agentId === agent.id).length} total calls
                          </span>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button variant="outline" size="sm" className="flex-1" asChild>
                            <Link href={`/app/agents`}>
                              <Settings className="h-3 w-3 mr-1" />
                              Configure
                            </Link>
                          </Button>
                          <Button variant="outline" size="sm" className="flex-1" asChild>
                            <Link href={`/app/calls`}>
                              <Phone className="h-3 w-3 mr-1" />
                              View Calls
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Agent Performance Summary */}
          {agents.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Agent Performance</CardTitle>
                  <CardDescription>Call handling statistics</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {agents.slice(0, 3).map((agent) => {
                    const agentCalls = calls.filter(c => c.agentId === agent.id).length;
                    const maxCalls = Math.max(...agents.map(a => calls.filter(c => c.agentId === a.id).length), 1);
                    return (
                      <div key={agent.id} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">{agent.name}</span>
                          <span className="text-muted-foreground">{agentCalls} calls</span>
                        </div>
                        <Progress value={(agentCalls / maxCalls) * 100} className="h-2" />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recommended Agents for Vending</CardTitle>
                  <CardDescription>Suggested agent configurations</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <PhoneIncoming className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">Customer Support Agent</div>
                      <div className="text-xs text-muted-foreground">Handle refunds, stuck products, general inquiries</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                      <PhoneOutgoing className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">Outbound Alert Agent</div>
                      <div className="text-xs text-muted-foreground">Notify locations about low stock or service needs</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                      <Wrench className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">Technical Support Agent</div>
                      <div className="text-xs text-muted-foreground">Troubleshoot machine issues with step-by-step guidance</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Machine Status Tab */}
        <TabsContent value="machines" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Machine Fleet</CardTitle>
              <CardDescription>
                Real-time status of all vending machines
              </CardDescription>
              <div className="flex items-center gap-2 pt-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by ID or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="max-w-sm"
                />
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Machine ID</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Stock Level</TableHead>
                    <TableHead>Last Service</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMachines.map((machine) => (
                    <TableRow key={machine.id}>
                      <TableCell className="font-medium">{machine.id}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          {machine.location}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(machine.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full">
                            <div
                              className={`h-2 rounded-full ${
                                machine.stockLevel > 60 ? "bg-green-500" :
                                machine.stockLevel > 30 ? "bg-yellow-500" : "bg-red-500"
                              }`}
                              style={{ width: `${machine.stockLevel}%` }}
                            />
                          </div>
                          <span className="text-sm">{machine.stockLevel}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{machine.lastService}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Support Calls Tab */}
        <TabsContent value="calls" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recent Support Calls</CardTitle>
                  <CardDescription>
                    Track and manage customer support interactions
                  </CardDescription>
                </div>
                <Button variant="outline" asChild>
                  <Link href="/app/calls">
                    View All Calls
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Machine</TableHead>
                    <TableHead>Issue</TableHead>
                    <TableHead>Handled By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockSupportCalls.map((call) => (
                    <TableRow key={call.id}>
                      <TableCell className="font-medium">{call.machineId}</TableCell>
                      <TableCell>{call.issue}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Bot className="h-4 w-4 text-muted-foreground" />
                          {call.handledBy}
                        </div>
                      </TableCell>
                      <TableCell>{getCallStatusBadge(call.status)}</TableCell>
                      <TableCell>{call.time}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          View Call
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Call Volume Trends</CardTitle>
                <CardDescription>Support calls over the last 7 days</CardDescription>
              </CardHeader>
              <CardContent className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Call analytics chart</p>
                  <p className="text-sm">Integrate with your analytics dashboard</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Issue Categories</CardTitle>
                <CardDescription>Most common support issues</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Product Stuck</span>
                    <span className="text-muted-foreground">35%</span>
                  </div>
                  <Progress value={35} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Payment Issues</span>
                    <span className="text-muted-foreground">28%</span>
                  </div>
                  <Progress value={28} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Refund Requests</span>
                    <span className="text-muted-foreground">22%</span>
                  </div>
                  <Progress value={22} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Machine Offline</span>
                    <span className="text-muted-foreground">15%</span>
                  </div>
                  <Progress value={15} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Agent Efficiency Metrics</CardTitle>
              <CardDescription>AI voice agent performance statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-3xl font-bold text-green-500">94%</div>
                  <div className="text-sm text-muted-foreground">Resolution Rate</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-3xl font-bold text-blue-500">2.5m</div>
                  <div className="text-sm text-muted-foreground">Avg Call Duration</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-3xl font-bold text-purple-500">4.8</div>
                  <div className="text-sm text-muted-foreground">Customer Rating</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-3xl font-bold text-amber-500">12s</div>
                  <div className="text-sm text-muted-foreground">Avg Response Time</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
